import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Building2,
  DollarSign,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Target,
  FileText,
  Camera,
  Video,
  Upload,
  X,
  Image,
  Film,
  FileCheck,
  Shield,
  ClipboardList,
  CheckCircle2,
  Home,
  Pencil,
  Save,
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { uploadToS3, fetchViewUrl } from "../lib/s3Upload";
import AddressAutocomplete from "./AddressAutocomplete";
import {
  formatCurrencyDisplay,
  formatCurrencyInput,
  parseCurrency,
} from "../lib/currency";
import useDealDocuments from "../hooks/useDealDocuments";

// Helper to get auth headers from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const PROPERTY_TYPES = [
  { value: "single_family", label: "Single Family" },
  { value: "multi_family", label: "Multi-Family" },
  { value: "apartment", label: "Apartment" },
  { value: "commercial", label: "Commercial" },
  { value: "land", label: "Land" },
  { value: "mixed_use", label: "Mixed Use" },
];

const DEAL_TYPES = [
  {
    value: "flip",
    label: "Flip",
    description: "Buy, renovate, and sell for profit",
  },
  {
    value: "rental",
    label: "Rental",
    description: "Buy and hold for rental income",
  },
  {
    value: "development",
    label: "Development",
    description: "Land development or new construction",
  },
];

const PHOTO_CATEGORIES = [
  { key: "neighborhood_street", label: "Neighborhood Street", required: false },
  { key: "front_exterior", label: "Front Exterior", required: false },
  { key: "living_room", label: "Living Room", required: false },
  { key: "kitchen", label: "Kitchen", required: false },
  { key: "bathroom", label: "Bathroom", required: false },
  { key: "bedroom", label: "Bedroom", required: false },
  { key: "left_exterior", label: "Left Exterior", required: false },
  { key: "right_exterior", label: "Right Exterior", required: false },
  { key: "backyard", label: "Backyard", required: false },
  { key: "garage", label: "Garage", required: false },
  { key: "damage_areas", label: "Damage Areas", required: false },
];

const VIDEO_CATEGORIES = [
  { key: "street_video", label: "Street Video" },
  { key: "exterior_video", label: "Exterior Video" },
  { key: "interior_video", label: "Interior Video" },
  { key: "damage_video", label: "Damage Video" },
];

const DealSubmissionModal = ({ open, onOpenChange, onSuccess, draftData }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submittedDealId, setSubmittedDealId] = useState(null);
  const [currentDraftId, setCurrentDraftId] = useState(null);

  const {
    documents: dealDocumentCategories,
    loadingDocs: loadingDocCategories,
  } = useDealDocuments();

  // Step 1: Property Details
  const [propertyAddress, setPropertyAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [squareFootage, setSquareFootage] = useState("");
  const [lotSize, setLotSize] = useState("");

  // Step 2: Deal Financials
  const [askingPrice, setAskingPrice] = useState("");
  const [wholesaleFee, setWholesaleFee] = useState("");
  const [estimatedRehabCost, setEstimatedRehabCost] = useState("");
  const [estimatedARV, setEstimatedARV] = useState("");

  // Step 3: Deal Type
  const [dealType, setDealType] = useState("");

  // Step 4: Scope of Work
  const [repairNotes, setRepairNotes] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  // Step 5: Media Uploads
  // Shape per entry:
  //   { file, preview, name }                   -> newly selected (will be uploaded)
  //   { existing: true, storage_path, name, viewUrl }  -> already on S3 from a prior submit
  const [photos, setPhotos] = useState({});
  const [videos, setVideos] = useState({});
  const photoInputRefs = useRef({});
  const videoInputRefs = useRef({});

  // Step 6: Document Uploads
  const [documents, setDocuments] = useState({});
  const documentInputRefs = useRef({});

  // Per-category upload progress (0-100) — only set while uploading
  // shape: { photos: {kitchen: 67}, videos: {...}, documents: {...} }
  const [uploadProgress, setUploadProgress] = useState({
    photos: {},
    videos: {},
    documents: {},
  });

  // Step 7: Agreement
  const [agreementChecked, setAgreementChecked] = useState(false);

  const resetForm = () => {
    setStep(1);
    setPropertyAddress("");
    setCity("");
    setState("");
    setZipCode("");
    setPropertyType("");
    setBedrooms("");
    setBathrooms("");
    setSquareFootage("");
    setLotSize("");
    setAskingPrice("");
    setWholesaleFee("");
    setEstimatedRehabCost("");
    setEstimatedARV("");
    setDealType("");
    setRepairNotes("");
    setAdditionalNotes("");
    setPhotos({});
    setVideos({});
    setDocuments({});
    setUploadProgress({ photos: {}, videos: {}, documents: {} });
    setAgreementChecked(false);
    setSubmittedDealId(null);
    setCurrentDraftId(null);
  };

  // Hydrate existing-media entries with short-lived view URLs (called whenever
  // draftData changes). We render these as <a> tags so the user can preview
  // and decide whether to replace them.
  const hydrateExistingMedia = async (media, setter) => {
    if (!media || Object.keys(media).length === 0) return;
    const next = {};
    await Promise.all(
      Object.entries(media).map(async ([key, meta]) => {
        if (!meta?.storage_path) return;
        let viewUrl = null;
        try {
          viewUrl = await fetchViewUrl(meta.storage_path);
        } catch (_) {
          // viewUrl stays null — UI shows non-clickable filename
        }
        next[key] = {
          existing: true,
          storage_path: meta.storage_path,
          content_type: meta.content_type,
          size: meta.size,
          name: meta.filename || key,
          viewUrl,
        };
      }),
    );
    setter((prev) => ({ ...next, ...prev })); // user-staged new files take precedence
  };

  // Load draft data when provided - using useEffect to properly handle prop changes
  useEffect(() => {
    if (draftData && open) {
      setCurrentDraftId(draftData.deal_id || null);
      setPropertyAddress(draftData.property_address || "");
      setCity(draftData.city || "");
      setState(draftData.state || "");
      setZipCode(draftData.zip_code || "");
      setPropertyType(draftData.property_type || "");
      setBedrooms(draftData.bedrooms != null ? String(draftData.bedrooms) : "");
      setBathrooms(
        draftData.bathrooms != null ? String(draftData.bathrooms) : "",
      );
      setSquareFootage(
        draftData.square_footage != null
          ? String(draftData.square_footage)
          : "",
      );
      setLotSize(draftData.lot_size || "");
      setAskingPrice(
        draftData.asking_price ? String(draftData.asking_price) : "",
      );
      setWholesaleFee(
        draftData.wholesale_fee ? String(draftData.wholesale_fee) : "",
      );
      setEstimatedRehabCost(
        draftData.estimated_rehab_cost
          ? String(draftData.estimated_rehab_cost)
          : "",
      );
      setEstimatedARV(
        draftData.estimated_arv ? String(draftData.estimated_arv) : "",
      );
      setDealType(draftData.deal_type || "");
      setRepairNotes(draftData.repair_notes || "");
      setAdditionalNotes(draftData.additional_notes || "");
      setAgreementChecked(draftData.agreement_accepted || false);
      // Seed existing media so users see preview links & can replace
      hydrateExistingMedia(draftData.photos, setPhotos);
      hydrateExistingMedia(draftData.videos, setVideos);
      hydrateExistingMedia(draftData.documents, setDocuments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftData, open]);

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        deal_id: currentDraftId || undefined,
        property_address: propertyAddress || undefined,
        city: city || undefined,
        state: state || undefined,
        zip_code: zipCode || undefined,
        property_type: propertyType || undefined,
        asking_price: parseCurrency(askingPrice) ?? undefined,
        wholesale_fee: parseCurrency(wholesaleFee) ?? undefined,
        estimated_rehab_cost: parseCurrency(estimatedRehabCost) ?? undefined,
        estimated_arv: parseCurrency(estimatedARV) ?? undefined,
        deal_type: dealType || undefined,
        repair_notes: repairNotes || undefined,
        additional_notes: additionalNotes || undefined,
        bedrooms: bedrooms ? parseInt(bedrooms) : undefined,
        bathrooms: bathrooms ? parseFloat(bathrooms) : undefined,
        square_footage: squareFootage ? parseInt(squareFootage) : undefined,
        lot_size: lotSize || undefined,
        agreement_accepted: agreementChecked || undefined,
      };
      const response = await axios.post(`${API}/deals/drafts`, payload, {
        withCredentials: true,
        headers,
      });
      setCurrentDraftId(response.data.deal_id);
      toast.success("Draft saved successfully");
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const isStep1Valid =
    propertyAddress && city && state && zipCode && propertyType;
  const isStep2Valid = askingPrice && estimatedRehabCost && estimatedARV;
  const isStep3Valid = dealType;
  const isStep4Valid = true;
  const isStep5Valid = true; // Photos are optional
  const isStep6Valid = true; // Documents are optional
  const isStep7Valid = agreementChecked;

  const handleNext = () => {
    if (step === 1 && isStep1Valid) setStep(2);
    else if (step === 2 && isStep2Valid) setStep(3);
    else if (step === 3 && isStep3Valid) setStep(4);
    else if (step === 4 && isStep4Valid) setStep(5);
    else if (step === 5 && isStep5Valid) setStep(6);
    else if (step === 6 && isStep6Valid) setStep(7);
    else if (step === 7 && isStep7Valid) setStep(8);
  };

  const handleBack = () => {
    if (step > 1 && step <= 8) setStep(step - 1);
  };

  const handleEditFromReview = (targetStep) => {
    setStep(targetStep);
  };

  const handlePhotoUpload = (category, event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be less than 10MB");
        return;
      }
      setPhotos((prev) => ({
        ...prev,
        [category]: {
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        },
      }));
    }
  };

  const handleVideoUpload = (category, event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        toast.error("Please upload a video file");
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video must be less than 100MB");
        return;
      }
      setVideos((prev) => ({
        ...prev,
        [category]: {
          file,
          name: file.name,
        },
      }));
    }
  };

  const handleDocumentUpload = (category, event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (
        !allowedTypes.includes(file.type) &&
        !file.name.endsWith(".pdf") &&
        !file.name.endsWith(".doc") &&
        !file.name.endsWith(".docx")
      ) {
        toast.error("Please upload a PDF or Word document");
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error("Document must be less than 25MB");
        return;
      }
      setDocuments((prev) => ({
        ...prev,
        [category]: {
          file,
          name: file.name,
        },
      }));
    }
  };

  const removePhoto = (category) => {
    setPhotos((prev) => {
      const newPhotos = { ...prev };
      if (newPhotos[category]?.preview) {
        URL.revokeObjectURL(newPhotos[category].preview);
      }
      delete newPhotos[category];
      return newPhotos;
    });
  };

  const removeVideo = (category) => {
    setVideos((prev) => {
      const newVideos = { ...prev };
      delete newVideos[category];
      return newVideos;
    });
  };

  const removeDocument = (category) => {
    setDocuments((prev) => {
      const newDocs = { ...prev };
      delete newDocs[category];
      return newDocs;
    });
  };

  const isEditingExistingDeal =
    currentDraftId && draftData && draftData.status !== "draft";

  // Upload newly-staged files (entries with `.file`) for one category to S3
  // and return a {key: {storage_path, ...}} map suitable for the deal payload.
  // Existing entries (already on S3) are preserved as-is.
  const uploadCategory = async (state, categoryName) => {
    const result = {};
    for (const [key, entry] of Object.entries(state)) {
      if (entry.existing && entry.storage_path) {
        result[key] = {
          storage_path: entry.storage_path,
          filename: entry.name,
          content_type: entry.content_type,
          size: entry.size,
        };
        continue;
      }
      if (entry.file) {
        const meta = await uploadToS3(entry.file, categoryName, (pct) => {
          setUploadProgress((prev) => ({
            ...prev,
            [categoryName]: { ...prev[categoryName], [key]: pct },
          }));
        });
        result[key] = meta;
      }
    }
    return result;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1) Upload any newly-staged files to S3 (sequential per category, parallel across categories)
      setUploadProgress({ photos: {}, videos: {}, documents: {} });
      const [photoMeta, videoMeta, docMeta] = await Promise.all([
        uploadCategory(photos, "photos"),
        uploadCategory(videos, "videos"),
        uploadCategory(documents, "documents"),
      ]);

      // 2) Build a JSON payload — both POST (new) and PUT (edit) accept the same shape
      const payload = {
        property_address: propertyAddress,
        city,
        state,
        zip_code: zipCode,
        property_type: propertyType,
        asking_price: parseCurrency(askingPrice),
        wholesale_fee: parseCurrency(wholesaleFee),
        estimated_rehab_cost: parseCurrency(estimatedRehabCost),
        estimated_arv: parseCurrency(estimatedARV),
        deal_type: dealType,
        repair_notes: repairNotes || null,
        additional_notes: additionalNotes || null,
        bedrooms: bedrooms ? parseInt(bedrooms) : null,
        bathrooms: bathrooms ? parseFloat(bathrooms) : null,
        square_footage: squareFootage ? parseInt(squareFootage) : null,
        lot_size: lotSize || null,
        agreement_accepted: true,
        photos: photoMeta,
        videos: videoMeta,
        documents: docMeta,
      };

      const headers = getAuthHeaders();
      let response;
      if (isEditingExistingDeal) {
        response = await axios.put(`${API}/deals/${currentDraftId}`, payload, {
          withCredentials: true,
          headers,
        });
      } else {
        response = await axios.post(`${API}/deals`, payload, {
          withCredentials: true,
          headers,
        });
      }

      setSubmittedDealId(response.data.deal_id);
      setStep(9);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Deal submit failed:", error);
      const message =
        error.response?.data?.detail ||
        error.message ||
        "Failed to submit deal";
      toast.error(
        typeof message === "string" ? message : "Failed to submit deal",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnother = () => {
    resetForm();
  };

  const handleGoToDashboard = () => {
    handleClose();
    navigate("/dashboard");
  };

  const formatDisplayCurrency = (value) => {
    return formatCurrencyDisplay(value);
  };

  const getPropertyTypeLabel = (value) => {
    return PROPERTY_TYPES.find((t) => t.value === value)?.label || value;
  };

  const getDealTypeLabel = (value) => {
    return DEAL_TYPES.find((t) => t.value === value)?.label || value;
  };

  const totalSteps = 9;
  const displayStep = step > 8 ? 8 : step;
  const progressPercent = step === 9 ? 100 : (displayStep / 8) * 100;

  return (
    <Dialog open={open} onOpenChange={step === 9 ? handleClose : handleClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        {step === 9 && (
          <DialogTitle className="sr-only">
            Deal submitted successfully
          </DialogTitle>
        )}
        <DialogDescription className="sr-only">
          {step === 9
            ? "Your deal was submitted successfully and is under review."
            : "Complete the deal submission form, including property details, financials, uploads, agreement, and review."}
        </DialogDescription>
        {step !== 9 && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Submit New Deal
              </DialogTitle>
            </DialogHeader>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Step {displayStep} of 8</span>
                <span>{Math.round(progressPercent)}% complete</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Progress Steps - Compact */}
            <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
              {[
                { num: 1, label: "Property" },
                { num: 2, label: "Financials" },
                { num: 3, label: "Type" },
                { num: 4, label: "Scope" },
                { num: 5, label: "Media" },
                { num: 6, label: "Docs" },
                { num: 7, label: "Agree" },
                { num: 8, label: "Review" },
              ].map((s, i) => (
                <div key={s.num} className="flex items-center">
                  <div className="flex flex-col items-center min-w-[40px]">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                        step > s.num
                          ? "bg-green-500 text-white"
                          : step === s.num
                            ? "bg-blue-500 text-white"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {step > s.num ? <Check className="w-3 h-3" /> : s.num}
                    </div>
                    <span
                      className={`text-[10px] mt-1 ${step >= s.num ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < 7 && (
                    <div
                      className={`w-4 h-0.5 mx-0.5 ${step > s.num ? "bg-green-500" : "bg-muted"}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Step 1: Property Details */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in" data-testid="deal-step-1">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-medium">Property Information</span>
            </div>

            <div className="space-y-2">
              <AddressAutocomplete
                id="propertyAddress"
                label="Property Address"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                onPlaceSelected={(addressData) => {
                  setPropertyAddress(
                    addressData.formatted_address || propertyAddress,
                  );
                  setCity(addressData.city || city);
                  setState(addressData.state || state);
                  setZipCode(addressData.zip_code || zipCode);
                }}
                placeholder="Enter property address"
                required={true}
                data-testid="deal-property-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Los Angeles"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="deal-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger data-testid="deal-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zipCode">Zip Code</Label>
                <Input
                  id="zipCode"
                  placeholder="90001"
                  value={zipCode}
                  onChange={(e) =>
                    setZipCode(
                      e.target.value.replace(/[^0-9]/g, "").slice(0, 5),
                    )
                  }
                  data-testid="deal-zip-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="propertyType">Property Type</Label>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger data-testid="deal-property-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  placeholder="3"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  data-testid="deal-bedrooms"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  step="0.5"
                  placeholder="2"
                  value={bathrooms}
                  onChange={(e) => setBathrooms(e.target.value)}
                  data-testid="deal-bathrooms"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="squareFootage">Sq. Footage</Label>
                <Input
                  id="squareFootage"
                  type="number"
                  placeholder="1,500"
                  value={squareFootage}
                  onChange={(e) => setSquareFootage(e.target.value)}
                  data-testid="deal-square-footage"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lotSize">Lot Size</Label>
                <Input
                  id="lotSize"
                  placeholder="0.25 acres"
                  value={lotSize}
                  onChange={(e) => setLotSize(e.target.value)}
                  data-testid="deal-lot-size"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Deal Financials */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in" data-testid="deal-step-2">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm font-medium">Financial Details</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="askingPrice">Selling Price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="askingPrice"
                  placeholder="250,000"
                  value={askingPrice}
                  inputMode="decimal"
                  onChange={(e) =>
                    setAskingPrice(formatCurrencyInput(e.target.value))
                  }
                  className="pl-7"
                  data-testid="deal-asking-price"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wholesaleFee">
                Wholesale Fee{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="wholesaleFee"
                  placeholder="10,000"
                  value={wholesaleFee}
                  inputMode="decimal"
                  onChange={(e) =>
                    setWholesaleFee(formatCurrencyInput(e.target.value))
                  }
                  className="pl-7"
                  data-testid="deal-wholesale-fee"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedRehabCost">Estimated Rehab Cost</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="estimatedRehabCost"
                  placeholder="50,000"
                  value={estimatedRehabCost}
                  inputMode="decimal"
                  onChange={(e) =>
                    setEstimatedRehabCost(formatCurrencyInput(e.target.value))
                  }
                  className="pl-7"
                  data-testid="deal-rehab-cost"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedARV">
                Estimated After Repair Value (ARV)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="estimatedARV"
                  placeholder="350,000"
                  value={estimatedARV}
                  inputMode="decimal"
                  onChange={(e) =>
                    setEstimatedARV(formatCurrencyInput(e.target.value))
                  }
                  className="pl-7"
                  data-testid="deal-arv"
                />
              </div>
            </div>

            {askingPrice && estimatedARV && (
              <div className="bg-muted/50 rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Deal Summary
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">
                    Potential Profit:
                  </span>
                  <span className="font-medium text-green-500">
                    {formatCurrencyDisplay(
                      (parseCurrency(estimatedARV) || 0) -
                        (parseCurrency(askingPrice) || 0) -
                        (parseCurrency(estimatedRehabCost) || 0),
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Deal Type */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in" data-testid="deal-step-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <Target className="w-5 h-5" />
              <span className="text-sm font-medium">Investment Strategy</span>
            </div>

            <p className="text-sm text-muted-foreground">
              Select the primary investment strategy for this property
            </p>

            <RadioGroup
              value={dealType}
              onValueChange={setDealType}
              className="space-y-3"
            >
              {DEAL_TYPES.map((type) => (
                <div
                  key={type.value}
                  className={`flex items-center space-x-4 p-4 border rounded-lg cursor-pointer transition-all ${
                    dealType === type.value
                      ? "border-blue-500 bg-blue-500/5"
                      : "border-border hover:border-blue-500/50"
                  }`}
                  onClick={() => setDealType(type.value)}
                  data-testid={`deal-type-${type.value}`}
                >
                  <RadioGroupItem value={type.value} id={type.value} />
                  <div className="flex-1">
                    <Label
                      htmlFor={type.value}
                      className="text-base font-medium cursor-pointer"
                    >
                      {type.label}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {type.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}

        {/* Step 4: Scope of Work */}
        {step === 4 && (
          <div className="space-y-4 animate-fade-in" data-testid="deal-step-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">Scope of Work</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="repairNotes">Repair/Renovation Notes</Label>
              <Textarea
                id="repairNotes"
                placeholder="Describe the repairs and renovations needed..."
                value={repairNotes}
                onChange={(e) => setRepairNotes(e.target.value)}
                className="min-h-[120px]"
                data-testid="deal-repair-notes"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Additional Deal Notes</Label>
              <Textarea
                id="additionalNotes"
                placeholder="Any other relevant information about this deal..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="min-h-[120px]"
                data-testid="deal-additional-notes"
              />
            </div>
          </div>
        )}

        {/* Step 5: Media Uploads */}
        {step === 5 && (
          <div className="space-y-6 animate-fade-in" data-testid="deal-step-5">
            {/* Photos Section */}
            <div>
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <Camera className="w-5 h-5" />
                <span className="text-sm font-medium">Photos</span>
                <span className="text-xs text-orange-500 ml-auto">
                  {Object.keys(photos).length}/{PHOTO_CATEGORIES.length}{" "}
                  uploaded
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {PHOTO_CATEGORIES.map((category) => {
                  const item = photos[category.key];
                  const progress = uploadProgress.photos[category.key];
                  const isUploading =
                    typeof progress === "number" && progress < 100 && loading;
                  return (
                    <div key={category.key} className="space-y-1">
                      <Label className="text-xs flex items-center gap-1">
                        {category.label}
                      </Label>

                      {item ? (
                        <div className="relative group">
                          {item.preview ? (
                            <img
                              src={item.preview}
                              alt={category.label}
                              className="w-full h-20 object-cover rounded-lg border border-border"
                            />
                          ) : item.existing && item.viewUrl ? (
                            <a
                              href={item.viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full h-20 rounded-lg border border-border overflow-hidden bg-muted"
                              data-testid={`photo-preview-${category.key}`}
                              title={item.name}
                            >
                              <img
                                src={item.viewUrl}
                                alt={item.name}
                                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </a>
                          ) : (
                            <div className="w-full h-20 bg-muted rounded-lg border border-border flex items-center justify-center">
                              <Image className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}

                          {isUploading && (
                            <div
                              className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center"
                              data-testid={`photo-progress-${category.key}`}
                            >
                              <div className="w-3/4 h-2 bg-white/30 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-white mt-1">
                                {progress}%
                              </span>
                            </div>
                          )}

                          <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              title="Replace"
                              onClick={() =>
                                photoInputRefs.current[category.key]?.click()
                              }
                              className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center"
                              data-testid={`photo-replace-${category.key}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              title="Remove"
                              onClick={() => removePhoto(category.key)}
                              className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            photoInputRefs.current[category.key]?.click()
                          }
                          className="w-full h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500/50 hover:bg-muted/50 transition-colors"
                        >
                          <Image className="w-5 h-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground mt-1">
                            Upload
                          </span>
                        </div>
                      )}

                      <input
                        ref={(el) =>
                          (photoInputRefs.current[category.key] = el)
                        }
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(category.key, e)}
                        data-testid={`photo-upload-${category.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Videos Section */}
            <div>
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <Video className="w-5 h-5" />
                <span className="text-sm font-medium">Videos</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  Optional
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {VIDEO_CATEGORIES.map((category) => {
                  const item = videos[category.key];
                  const progress = uploadProgress.videos[category.key];
                  const isUploading =
                    typeof progress === "number" && progress < 100 && loading;
                  return (
                    <div key={category.key} className="space-y-1">
                      <Label className="text-xs">{category.label}</Label>

                      {item ? (
                        <div className="relative group">
                          <div className="w-full h-20 bg-muted rounded-lg border border-border flex items-center justify-center px-2">
                            <div className="text-center w-full">
                              <Film className="w-5 h-5 text-muted-foreground mx-auto" />
                              {item.existing && item.viewUrl ? (
                                <a
                                  href={item.viewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-500 hover:underline truncate block max-w-full"
                                  data-testid={`video-preview-${category.key}`}
                                  title={item.name}
                                >
                                  {item.name}
                                </a>
                              ) : (
                                <span className="text-xs text-muted-foreground truncate block max-w-full">
                                  {item.name}
                                </span>
                              )}
                            </div>
                          </div>

                          {isUploading && (
                            <div
                              className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center"
                              data-testid={`video-progress-${category.key}`}
                            >
                              <div className="w-3/4 h-2 bg-white/30 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-white mt-1">
                                {progress}%
                              </span>
                            </div>
                          )}

                          <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              title="Replace"
                              onClick={() =>
                                videoInputRefs.current[category.key]?.click()
                              }
                              className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center"
                              data-testid={`video-replace-${category.key}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeVideo(category.key)}
                              className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            videoInputRefs.current[category.key]?.click()
                          }
                          className="w-full h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500/50 hover:bg-muted/50 transition-colors"
                        >
                          <Film className="w-5 h-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground mt-1">
                            Upload
                          </span>
                        </div>
                      )}

                      <input
                        ref={(el) =>
                          (videoInputRefs.current[category.key] = el)
                        }
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => handleVideoUpload(category.key, e)}
                        data-testid={`video-upload-${category.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Document Uploads */}
        {step === 6 && (
          <div className="space-y-4 animate-fade-in" data-testid="deal-step-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <FileCheck className="w-5 h-5" />
              <span className="text-sm font-medium">Document Uploads</span>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Upload any relevant deal documents. All documents are optional
                and can be added later.
              </p>
            </div>

            <div className="space-y-3">
              {loadingDocCategories ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading document types...</span>
                </div>
              ) : (
                dealDocumentCategories.map((doc) => {
                  const docKey = doc.name;
                  const item = documents[docKey];
                  const progress = uploadProgress.documents[docKey];
                  const isUploading =
                    typeof progress === "number" && progress < 100 && loading;
                  return (
                    <div key={docKey} className="space-y-1">
                      <Label className="text-sm flex items-center gap-1">
                        {doc.label}
                        <span className="text-muted-foreground text-xs">
                          {doc.required ? "(required)" : "(optional)"}
                        </span>
                      </Label>

                      {item ? (
                        <div className="relative">
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                              {item.existing && item.viewUrl ? (
                                <a
                                  href={item.viewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-500 hover:underline truncate"
                                  data-testid={`doc-preview-${docKey}`}
                                  title={item.name}
                                >
                                  {item.name}
                                </a>
                              ) : (
                                <span className="text-sm truncate">
                                  {item.name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                type="button"
                                title="Replace"
                                onClick={() =>
                                  documentInputRefs.current[docKey]?.click()
                                }
                                className="text-blue-500 hover:text-blue-600"
                                data-testid={`doc-replace-${docKey}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeDocument(docKey)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {isUploading && (
                            <div
                              className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden"
                              data-testid={`doc-progress-${docKey}`}
                            >
                              <div
                                className="h-full bg-blue-500 transition-all"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            documentInputRefs.current[docKey]?.click()
                          }
                          className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-blue-500/50 hover:bg-muted/50 transition-colors"
                        >
                          <Upload className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Upload PDF or Word document
                          </span>
                        </div>
                      )}

                      <input
                        ref={(el) => (documentInputRefs.current[docKey] = el)}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => handleDocumentUpload(docKey, e)}
                        data-testid={`doc-upload-${docKey}`}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step 7: Agreement and Consent */}
        {step === 7 && (
          <div className="space-y-6 animate-fade-in" data-testid="deal-step-7">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">Agreement and Consent</span>
            </div>

            <div className="bg-muted/50 rounded-lg p-6 border border-border">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="agreement"
                  checked={agreementChecked}
                  onCheckedChange={setAgreementChecked}
                  className="mt-1"
                  data-testid="deal-agreement-checkbox"
                />
                <Label
                  htmlFor="agreement"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  I agree that once{" "}
                  <span className="font-semibold text-blue-500">
                    CashFlow4U
                  </span>{" "}
                  signs a purchase agreement, this deal may be displayed to
                  investors and third-party platforms.
                </Label>
              </div>
            </div>

            {!agreementChecked && (
              <p className="text-sm text-muted-foreground text-center">
                You must accept the agreement to continue.
              </p>
            )}
          </div>
        )}

        {/* Step 8: Review Screen */}
        {step === 8 && (
          <div className="space-y-6 animate-fade-in" data-testid="deal-step-8">
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <ClipboardList className="w-5 h-5" />
              <span className="text-sm font-medium">
                Review Your Submission
              </span>
            </div>

            {/* Property Info */}
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Property Information
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditFromReview(1)}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Address:</span>
                <span>{propertyAddress}</span>
                <span className="text-muted-foreground">Location:</span>
                <span>
                  {city}, {state} {zipCode}
                </span>
                <span className="text-muted-foreground">Type:</span>
                <span>{getPropertyTypeLabel(propertyType)}</span>
              </div>
            </div>

            {/* Financials */}
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Financials
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditFromReview(2)}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Selling Price:</span>
                <span>{formatDisplayCurrency(askingPrice)}</span>
                <span className="text-muted-foreground">Wholesale Fee:</span>
                <span>
                  {wholesaleFee ? formatDisplayCurrency(wholesaleFee) : "N/A"}
                </span>
                <span className="text-muted-foreground">Rehab Cost:</span>
                <span>{formatDisplayCurrency(estimatedRehabCost)}</span>
                <span className="text-muted-foreground">ARV:</span>
                <span>{formatDisplayCurrency(estimatedARV)}</span>
                <span className="text-muted-foreground">Potential Profit:</span>
                <span className="text-green-500 font-medium">
                  {formatCurrencyDisplay(
                    (parseCurrency(estimatedARV) || 0) -
                      (parseCurrency(askingPrice) || 0) -
                      (parseCurrency(estimatedRehabCost) || 0),
                  )}
                </span>
              </div>
            </div>

            {/* Deal Type */}
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Target className="w-4 h-4" /> Deal Type
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditFromReview(3)}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
              <p className="text-sm">{getDealTypeLabel(dealType)}</p>
            </div>

            {/* Uploaded Files Summary */}
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <FileCheck className="w-4 h-4" /> Uploaded Files
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditFromReview(5)}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="flex items-center gap-1">
                  <Camera className="w-4 h-4 text-muted-foreground" />
                  <span>{Object.keys(photos).length} Photos</span>
                </div>
                <div className="flex items-center gap-1">
                  <Video className="w-4 h-4 text-muted-foreground" />
                  <span>{Object.keys(videos).length} Videos</span>
                </div>
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>{Object.keys(documents).length} Documents</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 9: Success State */}
        {step === 9 && (
          <div
            className="py-8 text-center animate-fade-in"
            data-testid="deal-step-9"
          >
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>

            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Deal Submitted Successfully!
            </h2>

            <p className="text-muted-foreground mb-6">
              Your deal has been submitted and is under review by{" "}
              <span className="font-semibold text-blue-500">CashFlow4U</span>.
            </p>

            <div className="bg-muted/50 rounded-lg p-4 mb-6 inline-block">
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-left">
                <span className="text-muted-foreground">Deal ID:</span>
                <span className="font-mono font-medium">{submittedDealId}</span>
                <span className="text-muted-foreground">Status:</span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                  Pending Review
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={handleSubmitAnother}
                data-testid="submit-another-deal-btn"
              >
                Submit Another Deal
              </Button>
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleGoToDashboard}
                data-testid="go-to-dashboard-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {step !== 9 && (
          <div className="flex justify-between pt-4 border-t border-border mt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={step === 1 ? handleClose : handleBack}
                data-testid="deal-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {step === 1 ? "Cancel" : "Back"}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSaveDraft}
                disabled={savingDraft || !propertyAddress}
                className="text-muted-foreground hover:text-foreground"
                data-testid="deal-save-draft-button"
              >
                {savingDraft ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {savingDraft ? "Saving..." : "Save Draft"}
              </Button>
            </div>

            {step < 8 ? (
              <Button
                onClick={handleNext}
                disabled={
                  (step === 1 && !isStep1Valid) ||
                  (step === 2 && !isStep2Valid) ||
                  (step === 3 && !isStep3Valid) ||
                  (step === 4 && !isStep4Valid) ||
                  (step === 5 && !isStep5Valid) ||
                  (step === 6 && !isStep6Valid) ||
                  (step === 7 && !isStep7Valid)
                }
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="deal-next-button"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="deal-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {isEditingExistingDeal ? "Resubmit Deal" : "Submit Deal"}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DealSubmissionModal;
